import { useState } from 'preact/hooks';
import { haptic } from '../utils/haptic';

interface Props {
  onSend: (data: string) => void;
  onScrollPage?: (pages: number) => void;
  isProdLocal?: boolean;
}

interface ToolbarButton {
  label: string;
  title: string;
  action: { type: 'send'; data: string } | { type: 'scroll'; pages: number };
  cls?: string;
}

const BUTTONS: ToolbarButton[] = [
  // Execution & Navigation
  { label: 'Enter', title: 'Execute command', action: { type: 'send', data: '\r' }, cls: 'ssh-tb-enter' },
  { label: 'Tab', title: 'Autocomplete', action: { type: 'send', data: '\t' } },
  { label: 'Esc', title: 'Escape', action: { type: 'send', data: '\x1b' } },
  { label: 'Space', title: 'Space', action: { type: 'send', data: ' ' } },
  { label: '↑', title: 'Previous command', action: { type: 'send', data: '\x1b[A' } },
  { label: '↓', title: 'Next command', action: { type: 'send', data: '\x1b[B' } },
  { label: '←', title: 'Move cursor left', action: { type: 'send', data: '\x1b[D' } },
  { label: '→', title: 'Move cursor right', action: { type: 'send', data: '\x1b[C' } },
  
  // Important Ctrl sequences
  { label: 'C-c', title: 'Interrupt (SIGINT)', action: { type: 'send', data: '\x03' }, cls: 'ssh-tb-danger' },
  { label: 'C-d', title: 'EOF / Exit', action: { type: 'send', data: '\x04' }, cls: 'ssh-tb-warn' },
  { label: 'C-l', title: 'Clear screen', action: { type: 'send', data: '\x0c' } },

  // Agent Shortcuts
  { label: 'claude', title: 'Launch Claude CLI with voice-assistant context', action: { type: 'send', data: 'claude --append-system-prompt-file /home/akrsmv/projects/voice-assistant/ssh-claude-context.txt\r' }, cls: 'ssh-tb-command' },
  { label: 'claude login', title: 'Login to Claude', action: { type: 'send', data: 'claude login\r' }, cls: 'ssh-tb-command' },
  { label: 'agy login', title: 'Login to Agy', action: { type: 'send', data: 'agy login\r' }, cls: 'ssh-tb-command' },

  // Quick commands
  { label: '⟳ Ops', title: 'Restart ops-panel (prod local only)', action: { type: 'send', data: 'sudo systemctl restart ops-panel\r' }, cls: 'ssh-tb-warn' },
  { label: 'Exit', title: 'Terminate SSH session', action: { type: 'send', data: 'exit\r' } },

  // Buffer Scroll & Page/Home/End
  { label: 'S-PgUp', title: 'Scroll buffer up', action: { type: 'scroll', pages: -1 }, cls: 'ssh-tb-scroll' },
  { label: 'S-PgDn', title: 'Scroll buffer down', action: { type: 'scroll', pages: 1 }, cls: 'ssh-tb-scroll' },
  { label: 'PgUp', title: 'Page up', action: { type: 'send', data: '\x1b[5~' } },
  { label: 'PgDn', title: 'Page down', action: { type: 'send', data: '\x1b[6~' } },
  { label: 'Home', title: 'Home', action: { type: 'send', data: '\x1b[H' } },
  { label: 'End', title: 'End', action: { type: 'send', data: '\x1b[F' } },

  // Less common Ctrl sequences
  { label: 'C-z', title: 'Suspend process', action: { type: 'send', data: '\x1a' }, cls: 'ssh-tb-warn' },
  { label: 'C-u', title: 'Clear line before cursor', action: { type: 'send', data: '\x15' } },
  { label: 'C-a', title: 'Start of line', action: { type: 'send', data: '\x01' } },
  { label: 'C-e', title: 'End of line', action: { type: 'send', data: '\x05' } },
  { label: 'C-r', title: 'Reverse search', action: { type: 'send', data: '\x12' } },
  { label: 'S-Tab', title: 'Shift+Tab (reverse)', action: { type: 'send', data: '\x1b[Z' } },
];

/** Horizontal expanding keyboard shortcut panel for mobile SSH.
 *  Collapsed: ⌨ toggle + always-visible Paste button (top-right below top bar).
 *  Expanded: horizontal rows of key buttons, flowing left from the toggle. */
export function SshToolbar({ onSend, onScrollPage, isProdLocal }: Props) {
  const [expanded, setExpanded] = useState(false);
  const visibleButtons = BUTTONS.filter(btn => btn.label !== '⟳ Ops' || isProdLocal);

  const handleClick = (btn: ToolbarButton) => {
    if (btn.action.type === 'scroll') {
      onScrollPage?.(btn.action.pages);
    } else {
      onSend(btn.action.data);
    }
    haptic(30);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        onSend(text);
        haptic(40);
      }
    } catch {
      // Clipboard API unavailable or permission denied — silently no-op
    }
  };

  return (
    <div class={`ssh-toolbar-h${expanded ? ' expanded' : ''}`}>
      <button
        class="ssh-tb-toggle"
        onClick={() => { setExpanded(!expanded); haptic(20); }}
        title={expanded ? 'Hide shortcuts' : 'Show keyboard shortcuts'}
      >
        {expanded ? '✕' : '⌨'}
      </button>
      <button
        class="ssh-tb-toggle ssh-tb-paste"
        onClick={handlePaste}
        title="Paste clipboard into terminal"
      >
        ⎘
      </button>
      {expanded && (
        <div class="ssh-tb-grid">
          {visibleButtons.map((btn) => (
            <button
              key={btn.label}
              class={`ssh-tb-btn${btn.cls ? ` ${btn.cls}` : ''}`}
              onClick={() => handleClick(btn)}
              title={btn.title}
            >
              {btn.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
